import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Testimonial } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import { doc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import ConfirmModal from '../../components/ConfirmModal';
import { useToast } from '../../hooks/useToast';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PlusIcon: React.FC<{className?: string}> = ({className}) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>);
const MenuIcon: React.FC<{ className?: string }> = ({ className }) => (<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>);

const SortableItem: React.FC<{ item: Testimonial, onDelete: (item: Testimonial) => void }> = ({ item, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <tr ref={setNodeRef} style={style}>
            <td className="px-5 py-3 border-b border-gray-200 bg-white text-sm w-12">
                <button {...attributes} {...listeners} className="cursor-grab p-2 text-gray-400 hover:text-gray-700">
                    <MenuIcon className="h-5 w-5" />
                </button>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-900 whitespace-no-wrap font-semibold">{item.clientName}</p>
                <p className="text-gray-600 whitespace-no-wrap text-xs">{item.companyName}</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p className="text-gray-600 whitespace-no-wrap max-w-md truncate italic">"{item.quote}"</p>
            </td>
            <td className="px-5 py-5 border-b border-gray-200 bg-white text-sm text-right whitespace-no-wrap">
                <Link to={`/admin/testimonial/edit/${item.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">Edit</Link>
                <button onClick={() => onDelete(item)} className="text-red-600 hover:text-red-900">Delete</button>
            </td>
        </tr>
    );
};

const AdminTestimonialsPage: React.FC = () => {
    const { data: testimonials, setData: setTestimonials, isLoading } = useCollection<Testimonial>('testimonials', { field: 'order', direction: 'asc' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Testimonial | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { showToast } = useToast();

    const openDeleteModal = (item: Testimonial) => {
        setItemToDelete(item);
        setIsModalOpen(true);
    };

    const handleDelete = async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'testimonials', itemToDelete.id));
            setTestimonials(prev => prev.filter(c => c.id !== itemToDelete.id));
            showToast('Testimonial deleted successfully.', { type: 'success' });
        } catch (error) {
            showToast("Error: Could not delete the testimonial.", { type: 'error' });
        } finally {
            setIsModalOpen(false);
            setItemToDelete(null);
            setIsDeleting(false);
        }
    };
    
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (testimonials && over && active.id !== over.id) {
            const oldIndex = testimonials.findIndex((p) => p.id === active.id);
            const newIndex = testimonials.findIndex((p) => p.id === over.id);
            const newOrder = arrayMove(testimonials, oldIndex, newIndex);
            setTestimonials(newOrder);

            try {
                const batch = writeBatch(db);
                newOrder.forEach((item, index) => {
                    batch.update(doc(db, 'testimonials', item.id), { order: index });
                });
                await batch.commit();
                showToast('Testimonial order updated.', { type: 'success' });
            } catch (error) {
                showToast('Failed to update testimonial order.', { type: 'error' });
                setTestimonials(testimonials);
            }
        }
    };

    return (
        <div>
            <ConfirmModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Testimonial"
                description={`Are you sure you want to delete this testimonial? This action cannot be undone.`}
                isConfirming={isDeleting}
            />
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">Manage Testimonials</h2>
                <Link to="/admin/testimonial/new" className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">
                   <PlusIcon className="h-5 w-5 mr-2"/>
                    Add New Testimonial
                </Link>
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                {isLoading && <p className="p-6 text-center">Loading testimonials...</p>}
                {!isLoading && (
                    <table className="min-w-full leading-normal">
                        <thead>
                            <tr>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 w-12"></th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Client</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Quote</th>
                                <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={testimonials?.map(s => s.id) || []} strategy={verticalListSortingStrategy}>
                                <tbody>
                                    {(testimonials || []).map(item => (
                                        <SortableItem key={item.id} item={item} onDelete={openDeleteModal} />
                                    ))}
                                </tbody>
                            </SortableContext>
                        </DndContext>
                    </table>
                )}
                {!isLoading && testimonials?.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        <p>No testimonials found.</p>
                        <p className="mt-2"><Link to="/admin/testimonial/new" className="text-indigo-600 hover:underline">Add your first one!</Link></p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminTestimonialsPage;
